import type { MeResponse } from '@mira/clients-shared';
import { useState, type FormEvent } from 'react';
import { createWebApiClient } from '../../api/webSession.js';
import { hrefForRoute, navigateTo } from '../../app/router.js';
import { showToast } from '../../app/toast.js';
import { Avatar, Button, ErrorState, Field, IconGlyph, MiraIcon, Select } from '../../ui/primitives/index.js';
import { DangerDialog, ListState } from '../../ui/patterns/index.js';
import { SettingsMenuRow, SettingsPrivacyRow } from '../../ui/domain/index.js';
import { webReferenceAssets } from '../../assets/catalog.js';
import { useApiQuery } from '../api-state.js';
import { safeDisplayName } from '../shared.js';

const {
  lakeHero: lakeHeroImage,
} = webReferenceAssets;

export function SettingsScreen(props: { preview?: boolean; screenId?: string; showLogout?: boolean }) {
  const [logoutError, setLogoutError] = useState(props.screenId === 'of_12_logout_failed' ? 'Sign-out failed right now. Please try again.' : '');
  const logoutBusy = props.preview && props.screenId === 'of_10_logging_out';
  const me = useApiQuery<MeResponse>({
    preview: Boolean(props.preview),
    previewData: { profile: { displayName: 'Lichen', gender: 'unknown' }, user: { id: 'preview', username: 'lichen_stat@126.com' } },
    emptyData: { profile: null, user: { id: '', username: '' } },
    previewError: 'Settings could not be loaded right now. Please try again.',
    previewState: settingsHomePreviewState(props.screenId),
    load: () => createWebApiClient().me(),
    deps: [],
  });

  async function logout() {
    if (props.preview) {
      navigateTo('login');
      return;
    }
    try {
      setLogoutError('');
      await createWebApiClient().logout();
      showToast('Signed out');
      navigateTo('login');
    } catch (err) {
      setLogoutError('Sign-out failed right now. Please try again.');
      navigateTo('settings');
    }
  }

  return (
    <article className="mira-business-screen mira-settings-screen">
      <header className="mira-settings-header">
        <BrandMira />
        <h1>Settings</h1>
        <p>Manage your profile, birth details, and privacy boundaries.</p>
      </header>
      <ListState state={me.state} errorTitle={me.error || 'Settings could not be loaded'}>
        <section className="mira-settings-profile-card">
          <Avatar decorative label={safeDisplayName(me.data)} />
          <div>
            <h2>{safeDisplayName(me.data)}</h2>
            <p>{usernameFromMe(me.data) || 'Signed in'}</p>
            <span><IconGlyph value="♧" /> Mira account</span>
          </div>
          <span aria-hidden="true"><IconGlyph value="♧" /></span>
        </section>
        {props.screenId === 'of_03_settings_saved' ? <p className="mira-soft-note">Settings saved.</p> : null}
        <nav className="mira-settings-menu" aria-label="Settings">
          <SettingsMenuRow icon="♙" title="Account & profile" href={hrefForRoute('settings-profile')} />
          <SettingsMenuRow icon="☾" title="Birth details" href={hrefForRoute('settings-birth')} />
          <SettingsMenuRow icon="▱" title="Change password" href={hrefForRoute('settings-password')} />
          <SettingsMenuRow icon="○" title="Privacy & data" href={hrefForRoute('settings-privacy')} />
          <SettingsMenuRow icon="§" title="Terms of Service" href={hrefForRoute('terms', {}, { source: 'settings-privacy' })} />
        </nav>
        <a className="mira-settings-logout-row" href="#/settings?sheet=logout"><span aria-hidden="true"><MiraIcon name="action.logout" /></span><strong>Sign out</strong><b aria-hidden="true"><IconGlyph value="›" /></b></a>
      </ListState>
      {logoutError ? <ErrorState title="Sign-out failed" body={logoutError} /> : null}
      {props.showLogout ? (
        <DangerDialog
          busy={logoutBusy}
          body="You can sign back in anytime after signing out."
          confirmLabel="Sign out"
          confirmLoadingLabel="Signing out…"
          onCancel={() => navigateTo('settings')}
          onConfirm={logout}
          title="Sign out of Mira?"
        />
      ) : null}
      <p className="mira-settings-footer">View and manage the details Mira keeps about you here.<span aria-hidden="true"><IconGlyph value="✦" /></span></p>
    </article>
  );
}

export function SettingsFormScreen(props: { kind: 'profile' | 'birth' | 'password' | 'privacy'; preview?: boolean; screenId?: string }) {
  const previewSaving = props.preview && props.screenId === 'of_02_settings_saving';
  const previewFailed = props.preview && ['of_04_settings_failed', 'settings_birth_02_failed', 'settings_password_02_failed'].includes(props.screenId || '');
  const [error, setError] = useState(previewFailed ? 'Settings could not be saved right now. Please try again.' : '');
  const [saving, setSaving] = useState(previewSaving);
  const me = useApiQuery<MeResponse>({
    preview: Boolean(props.preview),
    previewData: { profile: { displayName: 'Preview User', gender: 'unknown' }, birthProfile: { birthDate: '1992-08-18', birthHour: 8, birthMinute: 30, timezone: 'Asia/Shanghai', birthPlace: 'Shanghai' }, user: { id: 'preview', username: 'preview@example.test' } },
    emptyData: { profile: null, birthProfile: null, user: { id: '', username: '' } },
    previewState: settingsFormPreviewState(props.kind, props.screenId),
    load: () => createWebApiClient().me(),
    deps: [],
  });
  const profile = me.data.profile || {};
  const birthProfile = me.data.birthProfile || {};

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setError('');
    try {
      const api = createWebApiClient();
      if (props.kind === 'profile') {
        await api.updateProfile({
          displayName: String(form.get('displayName') || ''),
          gender: String(form.get('gender') || 'unknown') as 'female' | 'male' | 'other' | 'unknown',
        });
      } else if (props.kind === 'birth') {
        await api.updateBirthProfile({
          birthDate: String(form.get('birthDate') || '') || null,
          birthHour: numberOrNull(form.get('birthHour')),
          birthMinute: numberOrNull(form.get('birthMinute')),
          birthPlace: String(form.get('birthPlace') || '') || null,
          timezone: String(form.get('timezone') || '') || null,
        });
      } else if (props.kind === 'password') {
        const newPassword = String(form.get('newPassword') || '');
        const confirmPassword = String(form.get('confirmPassword') || '');
        if (newPassword !== confirmPassword) {
          throw new Error('The two new passwords do not match.');
        }
        if (newPassword.length < 8) {
          throw new Error('New password must be at least 8 characters.');
        }
        await api.changePassword({
          currentPassword: String(form.get('currentPassword') || ''),
          newPassword,
        });
      }
      showToast('Settings saved');
      navigateTo('settings');
    } catch (err) {
      setError(userFacingSettingsError(err));
    } finally {
      setSaving(false);
    }
  }

  if (props.kind === 'privacy') {
    return (
      <article className="mira-business-screen mira-settings-edit-screen mira-settings-privacy-screen">
        <SettingsEditTopbar title="Privacy & data" />
        <section className="mira-settings-privacy-hero">
          <div>
            <h2>Clear boundaries. Your data, your call.</h2>
            <p>Long-term memory, insights, and relationship details are separate. Review or delete each one on its own.</p>
          </div>
          <img alt="" src={lakeHeroImage} />
        </section>
        <nav className="mira-settings-privacy-list" aria-label="Privacy & data settings">
          <SettingsPrivacyRow icon="☷" title="Long-term memory" body="Long-term memory Mira uses for later understanding. Review or delete anytime." href={hrefForRoute('memory', {}, { tab: 'memory' })} />
          <SettingsPrivacyRow icon="◷" title="Insights" body="Reminders saved from conversations. They are not long-term memory and are never read back as memory." href={hrefForRoute('memory', {}, { tab: 'insights' })} />
          <SettingsPrivacyRow icon="♙" title="Relationships" body="Important relationship details you saved. View, edit, or delete them." href={hrefForRoute('relationships')} />
          <SettingsPrivacyRow icon="▱" title="Privacy Policy" body="How this version handles your data." href={hrefForRoute('privacy', {}, { source: 'settings' })} />
          <SettingsPrivacyRow icon="§" title="Terms of Service" body="The terms that apply when you use Mira." href={hrefForRoute('terms', {}, { source: 'settings-privacy' })} />
        </nav>
        <section className="mira-settings-control-card">
          <span aria-hidden="true"><IconGlyph value="▱" /></span>
          <div>
            <h2>You are in control</h2>
            <p>Memory, insights, and relationship details each have a clear entry point. Once deleted, they leave your library.</p>
            <a href={hrefForRoute('privacy', {}, { source: 'settings' })}>Read the full privacy policy <IconGlyph value="›" /></a>
          </div>
        </section>
      </article>
    );
  }

  if (props.kind === 'profile') {
    return (
      <article className="mira-business-screen mira-settings-edit-screen mira-settings-profile-edit-screen">
        <SettingsEditTopbar title="Profile" />
        <ListState state={me.state} errorTitle={me.error || 'Profile could not be loaded'}>
        <section className="mira-settings-avatar-hero">
          <div className="mira-settings-avatar-edit">
            <Avatar decorative label={safeDisplayName(me.data)} />
            <span aria-hidden="true"><IconGlyph value="▧" /></span>
          </div>
          <h2>Your avatar</h2>
          <p>Your avatar is generated from your name.</p>
        </section>
        <form className="mira-settings-rich-form" key={usernameFromMe(me.data) || 'profile'} onSubmit={submit}>
          <Field className="mira-settings-field" defaultValue={displayNameFromMe(me.data)} icon="♙" label="What should we call you" name="displayName" required />
          <Select className="mira-settings-field" defaultValue={profile.gender || 'unknown'} icon="♀" label="Gender" name="gender" trailing="⌄">
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other</option>
            <option value="unknown">Prefer not to say</option>
          </Select>
          <section className="mira-settings-info-card mira-settings-private-card">
            <span aria-hidden="true"><IconGlyph value="♧" /></span>
            <div>
              <h2>This helps Mira understand you</h2>
              <p>Your name and gender become part of your profile and shape how Mira talks with you.</p>
            </div>
          </section>
          {error || previewFailed ? <ErrorState title="Save failed" body={error || 'Settings could not be saved right now. Please try again.'} /> : null}
          <Button busy={saving || previewSaving} disabled={saving || previewSaving} type="submit">{saving || previewSaving ? 'Saving…' : 'Save'}</Button>
        </form>
        </ListState>
      </article>
    );
  }

  if (props.kind === 'birth') {
    return (
      <article className="mira-business-screen mira-settings-edit-screen mira-settings-birth-edit-screen">
        <SettingsEditTopbar title="Birth details" />
        <ListState state={me.state} errorTitle={me.error || 'Birth details could not be loaded'}>
        <section className="mira-settings-birth-hero">
          <div>
            <h2>Birth details ground Mira's baseline understanding of you.</h2>
            <p>Leave time blank if unsure. Mira works with the lower precision explicitly and never guesses a time for you.</p>
          </div>
          <img alt="" src={lakeHeroImage} />
        </section>
        <form className="mira-settings-rich-form" key={birthProfile.birthDate || birthProfile.birth_date || 'birth'} onSubmit={submit}>
          <Field className="mira-settings-field" defaultValue={formatBirthDate(birthProfile.birthDate || birthProfile.birth_date || '', birthProfile.timezone)} icon="▣" label="Birth date" name="birthDate" placeholder="YYYY-MM-DD" />
          <div className="mira-settings-inline-fields">
            <Field className="mira-settings-field" defaultValue={optionalNumberString(birthProfile.birthHour ?? birthProfile.birth_hour)} icon="◷" label="Birth hour" max="23" min="0" name="birthHour" type="number" />
            <Field className="mira-settings-field" defaultValue={optionalNumberString(birthProfile.birthMinute ?? birthProfile.birth_minute)} icon="◷" label="Birth minute" max="59" min="0" name="birthMinute" type="number" />
          </div>
          <p className="mira-settings-skip-time">Not sure about the time? Just leave hour and minute empty.</p>
          <Field className="mira-settings-field" defaultValue={birthProfile.timezone || ''} icon="◎" label="Time zone" name="timezone" placeholder="Asia/Shanghai" />
          <Field className="mira-settings-field" defaultValue={birthProfile.birthPlace || birthProfile.birth_place || ''} icon="⌖" label="Birth place" name="birthPlace" />
          {error || previewFailed ? <ErrorState title="Save failed" body={error || 'Settings could not be saved right now. Please try again.'} /> : null}
          <Button busy={saving || previewSaving} disabled={saving || previewSaving} type="submit">{saving || previewSaving ? 'Saving…' : 'Save'}</Button>
          <p className="mira-settings-secure-note"><IconGlyph value="▱" /> Every correction creates a new version and rebuilds the baseline understanding.</p>
        </form>
        </ListState>
      </article>
    );
  }

  return (
    <article className="mira-business-screen mira-settings-edit-screen mira-settings-password-screen">
      <SettingsEditTopbar title="Change password" label="Mira" />
      <header>
        <h1>Change password</h1>
        <p>New password must be at least 8 characters.</p>
      </header>
      <form className="mira-settings-rich-form" onSubmit={submit}>
          <Field className="mira-settings-field" label="Current password" name="currentPassword" placeholder="Enter current password" required trailing="◉" type="password" />
          <Field className="mira-settings-field" label="New password" minLength={8} name="newPassword" placeholder="Enter new password" required trailing="◉" type="password" />
          <Field className="mira-settings-field" label="Confirm new password" minLength={8} name="confirmPassword" placeholder="Re-enter new password" required trailing="◉" type="password" />
          <section className="mira-settings-password-rules">
            <span aria-hidden="true"><IconGlyph value="▱" /></span>
            <div>
              <h2>Password requirements</h2>
              <p><IconGlyph value="✓" /> At least 8 characters</p>
              <p><IconGlyph value="✓" /> Both entries must match</p>
            </div>
          </section>
          {error || previewFailed ? <ErrorState title="Save failed" body={error || 'Password could not be saved right now. Please try again.'} /> : null}
          <Button busy={saving || previewSaving} disabled={saving || previewSaving} type="submit">{saving || previewSaving ? 'Saving…' : 'Save new password'}</Button>
          <p className="mira-settings-secure-note"><IconGlyph value="▱" /> Your password is only used to verify your account.</p>
      </form>
    </article>
  );
}

function settingsHomePreviewState(screenId?: string): 'loading' | 'empty' | 'error' | 'ready' | undefined {
  if (screenId === 'settings_01_home_ready') return 'ready';
  if (screenId === 'settings_02_home_loading') return 'loading';
  if (screenId === 'settings_03_home_error') return 'error';
  return undefined;
}

function settingsFormPreviewState(kind: 'profile' | 'birth' | 'password' | 'privacy', screenId?: string): 'loading' | 'empty' | 'error' | 'ready' | undefined {
  if (kind === 'birth' && screenId === 'settings_birth_01_ready') return 'ready';
  if (kind === 'password' && screenId === 'settings_password_01_ready') return 'ready';
  return undefined;
}

function BrandMira() {
  return <div className="mira-settings-brand">Mira</div>;
}

function displayNameFromMe(me: unknown): string {
  if (!me || typeof me !== 'object') return '';
  const profile = 'profile' in me && me.profile && typeof me.profile === 'object' ? me.profile : null;
  if (profile && 'displayName' in profile && typeof profile.displayName === 'string') return profile.displayName;
  if (profile && 'display_name' in profile && typeof profile.display_name === 'string') return profile.display_name;
  const user = 'user' in me && me.user && typeof me.user === 'object' ? me.user : null;
  if (user && 'username' in user && typeof user.username === 'string') return user.username;
  return '';
}

function usernameFromMe(me: unknown): string {
  if (!me || typeof me !== 'object') return '';
  const user = 'user' in me && me.user && typeof me.user === 'object' ? me.user : null;
  if (user && 'username' in user && typeof user.username === 'string') return user.username;
  return '';
}

function SettingsEditTopbar(props: { label?: string; title: string }) {
  return (
    <nav className="mira-settings-edit-topbar" aria-label={`${props.title} navigation`}>
      <a href={hrefForRoute('settings')} aria-label="Back to settings"><MiraIcon name="action.back" />{props.label ? ' Back' : ''}</a>
      <strong data-brand={props.label ? true : undefined}>{props.label || props.title}</strong>
      <i />
    </nav>
  );
}

function userFacingSettingsError(error: unknown): string {
  const message = error instanceof Error ? error.message : '';
  if (/invalid credentials/i.test(message)) return 'Current password is incorrect.';
  if (/at least 8|>=8/i.test(message)) return 'New password must be at least 8 characters.';
  if (/must match/i.test(message)) return 'The two new passwords do not match.';
  return 'Settings could not be saved right now. Please try again.';
}

function numberOrNull(value: FormDataEntryValue | null): number | null {
  if (value === null || value === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function optionalNumberString(value?: number | null): string {
  return typeof value === 'number' && Number.isFinite(value) ? String(value).padStart(2, '0') : '';
}

// Backend returns ISO birth timestamps; the edit form shows the calendar date in the profile time zone.
function formatBirthDate(value: string | null | undefined, timezone?: string | null): string {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone || undefined, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  } catch {
    return value.slice(0, 10);
  }
}
